// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
}

// Surface misconfiguration early rather than failing deep inside the SDK.
export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId && firebaseConfig.appId,
)

if (!isFirebaseConfigured) {
  console.error(
    'Firebase is not configured. Set VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, ' +
      'VITE_FIREBASE_PROJECT_ID and VITE_FIREBASE_APP_ID in apps/provider-admin/.env',
  )
}

const app: FirebaseApp = initializeApp(firebaseConfig)
export const auth: Auth = getAuth(app)

// Identity Platform multi-tenancy: `auth.tenantId` is NOT persisted by the SDK
// across reloads/redirects. We pin it per signing-in user (home-realm discovery)
// and stash the resolved value in localStorage so it can be re-applied here —
// BEFORE any auth-state evaluation / getRedirectResult — keeping the federated
// redirect round-trip on the correct tenant even across a new tab/window.
// auth-context clears it once getRedirectResult resolves. Non-PHI session
// metadata. Guarded for non-browser (test) environments.
export const TENANT_STORAGE_KEY = 'qh.tenantId'

// Operator "act-as" org selection, persisted so it survives reload (re-applied
// in auth-context once the operator role is known). Non-PHI session metadata.
export const ACTING_AS_STORAGE_KEY = 'qh.actingAsOrg'

try {
  const saved = localStorage.getItem(TENANT_STORAGE_KEY)
  if (saved) auth.tenantId = saved
} catch {
  // localStorage unavailable (e.g. SSR/test) — nothing to restore.
}
